// AI Step 3: Data & Processing
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Textarea } from '../../../../components/ui/textarea';
import { Checkbox } from '../../../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../../../components/ui/accordion';
import { Database } from 'lucide-react';
import { DATA_CATEGORIES, SPECIAL_CATEGORIES } from '../constants';

export default function Step3DataProcessing({ formData, updateField }) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Data & Processing</h2>
        <p className="text-muted-foreground mt-1">Document the data used by the AI system.</p>
      </div>
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Database className="h-4 w-4 text-primary" />Training & Inference Data</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Training Data Sources</Label>
            <Textarea value={formData.training_data_sources || ''} onChange={e => updateField('training_data_sources', e.target.value)} placeholder="Describe where training data comes from..." className="mt-1.5" rows={2} />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox checked={formData.personal_data_involved} onCheckedChange={(checked) => updateField('personal_data_involved', checked)} />
            <span className="text-sm font-medium">Personal data is processed by this AI system</span>
          </label>
          {formData.personal_data_involved && (
            <>
              <Accordion type="multiple" className="w-full">
                {Object.entries(DATA_CATEGORIES).map(([category, items]) => (
                  <AccordionItem value={category} key={category}>
                    <AccordionTrigger className="text-sm capitalize">{category.replace('_', ' ')}</AccordionTrigger>
                    <AccordionContent>
                      <div className="grid grid-cols-2 gap-2">
                        {items.map(item => (
                          <label key={item} className="flex items-center gap-2 cursor-pointer">
                            <Checkbox checked={formData.data_categories.includes(item)}
                              onCheckedChange={(checked) => { const updated = checked ? [...formData.data_categories, item] : formData.data_categories.filter(c => c !== item); updateField('data_categories', updated); }} />
                            <span className="text-sm">{item}</span>
                          </label>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
              <Card className="border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/20">
                <CardHeader className="pb-2"><CardTitle className="text-sm text-amber-700 dark:text-amber-400">Special Category Data</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {SPECIAL_CATEGORIES.map(cat => (
                      <label key={cat} className="flex items-center gap-2 cursor-pointer">
                        <Checkbox checked={formData.special_categories.includes(cat)}
                          onCheckedChange={(checked) => { const updated = checked ? [...formData.special_categories, cat] : formData.special_categories.filter(c => c !== cat); updateField('special_categories', updated); }} />
                        <span className="text-sm">{cat}</span>
                      </label>
                    ))}
                  </div>
                </CardContent>
              </Card>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Data Volume</Label>
                  <Select value={formData.data_volume} onValueChange={v => updateField('data_volume', v)}>
                    <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select volume" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="minimal">Minimal (&lt;1,000 records)</SelectItem>
                      <SelectItem value="small">Small (1K-10K)</SelectItem>
                      <SelectItem value="medium">Medium (10K-100K)</SelectItem>
                      <SelectItem value="large">Large (100K-1M)</SelectItem>
                      <SelectItem value="massive">Massive (&gt;1M)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Data Retention</Label>
                  <Input value={formData.data_retention || ''} onChange={e => updateField('data_retention', e.target.value)} placeholder="e.g., 2 years" className="mt-1.5" />
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={formData.cross_border_processing} onCheckedChange={(checked) => updateField('cross_border_processing', checked)} />
                <span className="text-sm font-medium">Cross-border data processing</span>
              </label>
              {formData.cross_border_processing && (
                <div>
                  <Label>Transfer Countries</Label>
                  <Input value={(formData.transfer_countries || []).join(', ')} onChange={e => updateField('transfer_countries', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} placeholder="e.g., USA, UK, Singapore" className="mt-1.5" />
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
